/**
 * Jeu d'icônes CURÉ pour le bloc de contenu `Icon` (Lot 4).
 *
 * On NE fait PAS d'import dynamique non borné de `lucide-react` (taille de
 * bundle + risque de nom invalide) : on mappe explicitement un petit ensemble
 * de noms autorisés → composant lucide. Le nom (`name`) est une prop
 * STRUCTURELLE (pas de texte traduisible) ; un nom inconnu retombe sur un
 * fallback neutre côté rendu.
 *
 * PARITÉ BO ↔ FRONT : un module identique existe côté front public
 * (`inventozen-frontpublic/src/cms-puck/icons.ts`) — mêmes noms, mêmes couleurs.
 */
import {
  Star,
  Heart,
  Check,
  Info,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Clock,
  Image,
  Camera,
  Palette,
  Brush,
  Award,
  Sparkles,
  ArrowRight,
  ExternalLink,
  Quote,
  Globe,
  Instagram,
  Facebook,
  Twitter,
  Youtube,
  Linkedin,
  type LucideIcon,
} from "lucide-react";
import {
  normalizeColorRef,
  resolveColorRef,
  type ColorRef,
} from "@/lib/cms-puck/block-style";
import { type CmsColor } from "@/lib/cms/cms-theme.constants";

/** Map nom → composant lucide. Source de vérité du jeu d'icônes autorisé. */
export const CMS_ICONS = {
  star: Star,
  heart: Heart,
  check: Check,
  info: Info,
  mail: Mail,
  phone: Phone,
  mapPin: MapPin,
  calendar: Calendar,
  clock: Clock,
  image: Image,
  camera: Camera,
  palette: Palette,
  brush: Brush,
  award: Award,
  sparkles: Sparkles,
  arrowRight: ArrowRight,
  externalLink: ExternalLink,
  quote: Quote,
  globe: Globe,
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
  youtube: Youtube,
  linkedin: Linkedin,
} satisfies Record<string, LucideIcon>;

export type CmsIconName = keyof typeof CMS_ICONS;

/** Liste ordonnée des noms (pour les `options` du select Puck). */
export const CMS_ICON_NAMES = Object.keys(CMS_ICONS) as CmsIconName[];

/** @deprecated Anciennes clés prédéfinies — rétro-compat rendu uniquement. */
export const CMS_ICON_COLORS = {
  current: "currentColor",
  slate: "#475569",
  black: "#0f172a",
  white: "#ffffff",
  red: "#dc2626",
  amber: "#d97706",
  green: "#16a34a",
  blue: "#2563eb",
} satisfies Record<string, string>;

export type CmsIconColor = keyof typeof CMS_ICON_COLORS;

/** Résout un nom (structurel) en composant ; fallback `star` si inconnu. */
export function resolveCmsIcon(name: string | undefined): LucideIcon {
  return CMS_ICONS[(name ?? "") as CmsIconName] ?? CMS_ICONS.star;
}

/** Normalise la couleur d'icône (ColorRef ou legacy string) pour l'éditeur. */
export function normalizeIconColor(value: unknown): ColorRef {
  if (value == null) return null;
  if (typeof value === "string") {
    if (value in CMS_ICON_COLORS) {
      const hex = CMS_ICON_COLORS[value as CmsIconColor];
      return hex === "currentColor" ? null : { source: "custom", hex };
    }
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value)) {
      return { source: "custom", hex: value };
    }
    return null;
  }
  return normalizeColorRef(value);
}

/**
 * Résout la couleur d'icône pour le rendu Lucide (`color` prop).
 * `null` / hérite / legacy `current` → `currentColor`.
 */
export function resolveIconColor(
  palette: CmsColor[],
  value: unknown,
): string {
  if (typeof value === "string") {
    if (value in CMS_ICON_COLORS) {
      return CMS_ICON_COLORS[value as CmsIconColor];
    }
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value)) {
      return value;
    }
    return CMS_ICON_COLORS.current;
  }
  return resolveColorRef(palette, normalizeIconColor(value)) ?? "currentColor";
}

/** @deprecated Utiliser `resolveIconColor(palette, value)`. */
export function resolveCmsIconColor(color: string | undefined): string {
  return resolveIconColor([], color);
}
